/**
 * Anonymous Core Web Vitals collector. Queues metrics reported by the vendored
 * web-vitals library and beacons them to the endpoint configured in theme
 * settings when the page is hidden. Payloads carry no cookies and no
 * identifiers — never add customer or session ids here.
 */
(() => {
  'use strict';

  if (window.PerformanceVitals) return;
  window.PerformanceVitals = Object.freeze({ version: 1 });

  if (document.documentElement.dataset.appView === 'true') return;

  const settings = window.themeSettings;
  if (!settings || !settings.perfEndpoint) return;
  if (!window.webVitals || typeof window.webVitals.onLCP !== 'function') return;

  const sampleRate = typeof settings.perfSampleRate === 'number' ? settings.perfSampleRate : 100;
  if (Math.random() * 100 >= sampleRate) return;

  const endpoint = settings.perfEndpoint;

  const ATTRIBUTION_FIELDS = {
    LCP: ['target', 'url', 'timeToFirstByte', 'resourceLoadDelay', 'resourceLoadDuration', 'elementRenderDelay'],
    CLS: ['largestShiftTarget', 'largestShiftValue'],
    INP: ['interactionTarget', 'interactionType', 'inputDelay', 'processingDuration', 'presentationDelay']
  };

  // Last write per metric id: a metric that updates after a flush (INP/CLS on a
  // re-shown tab) is re-sent under the same id and deduped at query time.
  const queue = new Map();

  /**
   * Generate an anonymous per-pageview id.
   * @returns {string} A UUID, or a timestamp-random fallback where crypto.randomUUID is unavailable.
   */
  function generatePageviewId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Classify the user agent into a coarse engine bucket, formatted
   * `<class>-<major>` (bare class when no major version is found). The raw UA
   * string is never sent. In-app webviews must never land in the `safari`
   * bucket, and every Chromium engine must land in a Chromium-engine bucket —
   * the CLS report slice keys off it. iOS ports of Chrome/Firefox/Edge
   * deliberately classify as `safari`: they are WebKit and measure like Safari.
   * @returns {string} One of webview-ios, webview-android, chromium, safari, firefox or other.
   */
  function classifyBrowser() {
    const ua = navigator.userAgent || '';
    const chromeMajor = (ua.match(/Chrome\/(\d+)/) || [])[1];
    const iosMajor = (ua.match(/OS (\d+)_/) || [])[1];
    const inApp = /FBAN|FBAV|Instagram|musical_ly|BytedanceWebview/.test(ua);

    if (/iPhone|iPad|iPod/.test(ua) && (!/Safari\//.test(ua) || inApp)) {
      return iosMajor ? `webview-ios-${iosMajor}` : 'webview-ios';
    }
    if (/; wv\)/.test(ua)) {
      return chromeMajor ? `webview-android-${chromeMajor}` : 'webview-android';
    }
    if (chromeMajor) {
      return `chromium-${chromeMajor}`;
    }
    if (/AppleWebKit/.test(ua) && /Safari\//.test(ua)) {
      const safariMajor = (ua.match(/Version\/(\d+)/) || [])[1] || iosMajor;
      return safariMajor ? `safari-${safariMajor}` : 'safari';
    }
    const firefoxMajor = (ua.match(/Firefox\/(\d+)/) || [])[1];
    if (firefoxMajor) {
      return `firefox-${firefoxMajor}`;
    }
    return 'other';
  }

  const context = {
    pageviewId: generatePageviewId(),
    path: window.location.pathname,
    template: settings.template || '',
    market: settings.athosRegion || '',
    theme: settings.themeVersion || '',
    viewport: window.innerWidth < 768 ? 'mobile' : 'desktop',
    connection: navigator.connection && navigator.connection.effectiveType ? navigator.connection.effectiveType : '',
    browser: classifyBrowser()
  };

  /**
   * Trim a reported metric to the fields the pipeline stores — never the raw
   * performance entries.
   * @param {Object} metric - A metric object from the web-vitals library.
   * @returns {Object} The serialised metric row.
   */
  function serialiseMetric(metric) {
    const row = {
      name: metric.name,
      value: metric.name === 'CLS' ? Math.round(metric.value * 10000) / 10000 : Math.round(metric.value),
      rating: metric.rating,
      id: metric.id,
      navigationType: metric.navigationType
    };
    const fields = ATTRIBUTION_FIELDS[metric.name];
    if (fields && metric.attribution) {
      row.attribution = {};
      fields.forEach((field) => {
        if (metric.attribution[field] !== undefined) {
          row.attribution[field] = metric.attribution[field];
        }
      });
    }
    return row;
  }

  /**
   * Record a metric report, keeping only the latest report per metric id.
   * @param {Object} metric - A metric object from the web-vitals library.
   * @returns {void}
   */
  function handleMetric(metric) {
    queue.set(metric.id, serialiseMetric(metric));
  }

  /**
   * Send all queued metrics to the collector endpoint and clear the queue.
   * Failures are swallowed — telemetry must never break the page.
   * @returns {void}
   */
  function flush() {
    if (queue.size === 0) return;
    const payload = JSON.stringify({ context, metrics: Array.from(queue.values()) });
    queue.clear();
    let sent = false;
    if (typeof navigator.sendBeacon === 'function') {
      try {
        sent = navigator.sendBeacon(endpoint, payload);
      } catch (error) {
        sent = false;
      }
    }
    if (!sent) {
      try {
        fetch(endpoint, { method: 'POST', body: payload, keepalive: true }).catch(() => {});
      } catch (error) {
        // Swallow: fetch can throw synchronously in old browsers.
      }
    }
  }

  ['onLCP', 'onCLS', 'onINP', 'onTTFB', 'onFCP'].forEach((name) => {
    if (typeof window.webVitals[name] === 'function') {
      window.webVitals[name](handleMetric);
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
})();
